import { useEffect } from 'react'
import { Modal, Form, Input, Select, Switch } from 'antd'
import { UserModalProps, UserFormData, UserStatus } from '../types'
import {
  UserStatusLabels,
  USER_CODE_VALIDATION,
  USER_NAME_VALIDATION,
} from '../constants'

const { Option } = Select

const UserModal = ({
  isOpen,
  onClose,
  onSave,
  user,
  loading = false,
  roles = [],
}: UserModalProps) => {
  const [form] = Form.useForm<UserFormData>()
  const isEditMode = !!user

  // 编辑模式：填充现有数据
  useEffect(() => {
    if (isOpen) {
      if (user) {
        form.setFieldsValue({
          code: user.code,
          name: user.name,
          avatar: user.avatar,
          status: user.status,
          roleIds: (user.roleIds || []).map((id) => String(id)),
        })
      } else {
        form.resetFields()
      }
    }
  }, [isOpen, user, form])

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData: UserFormData = {
      code: values.code,
      name: values.name,
      avatar: values.avatar,
      status: values.status,
      roleIds: (values.roleIds || []).map((id) => String(id)),
    }
    onSave(formData)
  }

  // 处理取消
  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={user ? '编辑用户信息' : '新增用户'}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="code"
          label="用户编码"
          rules={[
            { required: true, message: USER_CODE_VALIDATION.REQUIRED_MESSAGE },
            {
              min: USER_CODE_VALIDATION.MIN_LENGTH,
              message: USER_CODE_VALIDATION.MIN_MESSAGE,
            },
            {
              max: USER_CODE_VALIDATION.MAX_LENGTH,
              message: USER_CODE_VALIDATION.MAX_MESSAGE,
            },
            {
              pattern: USER_CODE_VALIDATION.PATTERN,
              message: USER_CODE_VALIDATION.PATTERN_MESSAGE,
            },
          ]}
        >
          <Input
            placeholder="请输入用户编码"
            maxLength={USER_CODE_VALIDATION.MAX_LENGTH}
            disabled={isEditMode}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="用户名"
          rules={[
            { required: true, message: USER_NAME_VALIDATION.REQUIRED_MESSAGE },
            {
              max: USER_NAME_VALIDATION.MAX_LENGTH,
              message: USER_NAME_VALIDATION.MAX_MESSAGE,
            },
          ]}
        >
          <Input
            placeholder="请输入用户名"
            maxLength={USER_NAME_VALIDATION.MAX_LENGTH}
          />
        </Form.Item>

        {isEditMode && (
          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
            getValueFromEvent={(checked) =>
              checked ? UserStatus.ACTIVE : UserStatus.INACTIVE
            }
            getValueProps={(value) => ({
              checked: value === UserStatus.ACTIVE,
            })}
          >
            <Switch
              checkedChildren={UserStatusLabels[UserStatus.ACTIVE]}
              unCheckedChildren={UserStatusLabels[UserStatus.INACTIVE]}
            />
          </Form.Item>
        )}

        <Form.Item
          name="roleIds"
          label="角色"
          rules={[
            {
              required: true,
              type: 'array',
              min: 1,
              message: '请至少选择一个角色',
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="请选择角色（至少一个）"
            allowClear
            optionFilterProp="children"
          >
            {roles.map((role) => (
              <Option key={String(role.id)} value={String(role.id)}>
                {role.name} ({role.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="avatar" label="头像URL">
          <Input placeholder="请输入头像URL（可选）" />¬
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default UserModal
